"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toolDefinitionProviderMetadata = toolDefinitionProviderMetadata;
exports.parseToolDefinitionValue = parseToolDefinitionValue;
exports.toolDefinition = toolDefinition;
const conventions_1 = require("../../conventions");
const json_1 = require("../utils/json");
// Keys consumed as definition fields by SOME dialect's extraction; everything
// else on a definition record is provider trivia lifted verbatim into
// providerMetadata. A union by necessity: metadata inference must know every
// dialect's consumed keys regardless of which extractor claimed the item.
const CONSUMED_DEFINITION_KEYS = new Set([
    "name",
    "description",
    "desc", // loose/traceloop
    "parameters", // openai, gemini
    "parameters_json_schema", // pydantic ai
    "input_schema", // anthropic
    "inputSchema", // ai sdk / mcp
    "format", // openai custom tools
    "function", // openai function-tool wrapper
    "custom", // openai custom-tool wrapper
    "type",
    "providerMetadata",
]);
/** Provider trivia beside the consumed definition fields, wrapper and inner
 * definition merged, explicit `providerMetadata` winning on collisions. */
function toolDefinitionProviderMetadata(wrapper, definition) {
    const explicit = (0, json_1.asRecord)(wrapper.providerMetadata);
    return (0, json_1.remainingProviderMetadata)([wrapper, definition], CONSUMED_DEFINITION_KEYS, explicit);
}
/**
 * Loose fallback: bare `{name, description | desc, parameters}` items
 * (`parameters` is shared openai/gemini vocabulary) and — with the
 * carrier's allowance — nameless provider built-ins, named by their
 * `id`/`type` (openai `web_search_preview`, anthropic server tools, gemini
 * `googleSearch`-style groups).
 */
function normalizeLooseToolDefinition(value, options) {
    const rawName = value.name ??
        (options.allowProviderToolWithoutName
            ? (value.id ?? (value.type !== "function" ? value.type : undefined))
            : undefined);
    return toolDefinition({
        name: rawName,
        description: value.description ?? value.desc /* loose/traceloop */,
        inputSchema: value.parameters /* openai, gemini */,
        type: value.type,
        providerMetadata: toolDefinitionProviderMetadata(value, value),
    });
}
function normalizeDefinitionItem(value, options) {
    if (!(0, json_1.isRecord)(value))
        return null;
    for (const provider of conventions_1.registeredProviders) {
        const result = provider.tryNormalizeToolDefinition?.(value);
        if (result?.matched)
            return result.value;
    }
    return normalizeLooseToolDefinition(value, options);
}
function parseToolDefinitionValue(value, options = {}) {
    const parsed = (0, json_1.parseIfString)(value);
    if (Array.isArray(parsed)) {
        const results = parsed.map((item) => normalizeDefinitionItem((0, json_1.parseIfString)(item), options));
        return {
            definitions: results.filter((def) => def !== null),
            fullyParsed: results.every((def) => def !== null),
        };
    }
    if (!(0, json_1.isRecord)(parsed))
        return { definitions: [], fullyParsed: false };
    const singleDefinition = normalizeDefinitionItem(parsed, options);
    if (singleDefinition) {
        return { definitions: [singleDefinition], fullyParsed: true };
    }
    if (!options.allowToolMap)
        return { definitions: [], fullyParsed: false };
    // Some instrumentation exports definitions as a map keyed by tool name.
    const definitions = [];
    let fullyParsed = true;
    for (const [name, rawDefinition] of Object.entries(parsed)) {
        const definition = (0, json_1.parseRecord)(rawDefinition);
        if (!definition) {
            fullyParsed = false;
            continue;
        }
        const normalized = normalizeDefinitionItem({ name, ...definition }, {});
        if (normalized)
            definitions.push(normalized);
        else
            fullyParsed = false;
    }
    return { definitions, fullyParsed };
}
/** Canonical tool-definition constructor — same contract as the tool-part
 * builders: callers extract their own dialect's fields. */
function toolDefinition(fields) {
    if (typeof fields.name !== "string" || fields.name.length === 0)
        return null;
    return (0, json_1.compact)({
        name: fields.name,
        description: typeof fields.description === "string" ? fields.description : undefined,
        inputSchema: fields.inputSchema === undefined
            ? undefined
            : (0, json_1.toJsonValue)((0, json_1.parseIfString)(fields.inputSchema)),
        type: typeof fields.type === "string" ? fields.type : undefined,
        providerMetadata: fields.providerMetadata,
    });
}
